import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, X, Sparkles } from 'lucide-react';
import { ConvertedImage, formatBytes, calculateCompressionPercentage } from '../services/imageService';

interface ConvertedImagesTableProps {
  images: ConvertedImage[];
  onDownload: (image: ConvertedImage) => void;
  onDownloadAll: () => void;
  onRemove: (id: string) => void;
  onClear: () => void;
  onPreview: (url: string) => void;
}

const ConvertedImagesTable: React.FC<ConvertedImagesTableProps> = ({
  images,
  onDownload,
  onDownloadAll,
  onRemove,
  onClear,
  onPreview
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const { t } = useTranslation();

  if (!Array.isArray(images) || images.length === 0) return null;

  const handleSingleDownload = async (image: ConvertedImage) => {
    try {
      await onDownload(image);
      setError(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to download image';
      console.error('Error downloading image:', errorMessage);
      setError(errorMessage);
    }
  };

  return (
    <section className="mb-8" aria-labelledby="converted-images">
      <div className="card">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <h2 id="converted-images" className="text-xl flex items-center m-0">
              <Sparkles className="mr-2 text-primary-500" size={20} aria-hidden="true" />
              Converted images ({images.length})
            </h2>
            
            <div className="flex space-x-3">
              <button 
                onClick={onDownloadAll}
                className="btn btn-primary"
                aria-label={t('common.downloadAll')}
              >
                <Download size={16} aria-hidden="true" />
                {t('common.downloadAll')}
              </button>
              <button 
                onClick={onClear}
                className="btn btn-secondary"
                aria-label={t('common.clearAll')}
              >
                <X size={16} aria-hidden="true" />
                {t('common.clearAll')}
              </button>
            </div>
          </div>
          
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="table" aria-labelledby="converted-images">
            <thead className="table-header">
              <tr>
                <th scope="col" className="px-6 py-4">{t('convertedImages.headers.preview')}</th>
                <th scope="col" className="px-6 py-4">{t('convertedImages.headers.fileName')}</th>
                <th scope="col" className="px-6 py-4">{t('convertedImages.headers.originalSize')}</th>
                <th scope="col" className="px-6 py-4">{t('convertedImages.headers.convertedSize')}</th>
                <th scope="col" className="px-6 py-4">{t('convertedImages.headers.reduction')}</th>
                <th scope="col" className="px-6 py-4">Applied Compression</th>
                <th scope="col" className="px-6 py-4">{t('convertedImages.headers.format')}</th>
                <th scope="col" className="px-6 py-4">{t('convertedImages.headers.actions')}</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {images.map((image) => (
                <tr key={image.id} className="table-row">
                  <td className="table-cell">
                    <div 
                      className="h-12 w-12 rounded-[1rem] bg-gray-100 flex items-center justify-center cursor-pointer overflow-hidden"
                      onClick={() => onPreview(image.previewUrl)}
                      role="button"
                      tabIndex={0}
                      aria-label={`Preview converted ${image.originalFile.name}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          onPreview(image.previewUrl);
                        }
                      }}
                    >
                      <img 
                        src={image.previewUrl} 
                        alt={`Preview of converted ${image.originalFile.name}`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="text-sm text-gray-900 truncate max-w-[150px]">
                      {image.originalFile.name.split('.')[0]}.{image.format}
                    </div>
                  </td>
                  <td className="table-cell">
                    {formatBytes(image.originalSize)}
                  </td>
                  <td className="table-cell">
                    {formatBytes(image.convertedSize)}
                  </td>
                  <td className="table-cell">
                    <span className={image.convertedSize < image.originalSize ? "text-green-600" : "text-orange-600"}>
                      {image.convertedSize < image.originalSize 
                        ? `-${calculateCompressionPercentage(image.originalSize, image.convertedSize)}%` 
                        : `+${calculateCompressionPercentage(image.convertedSize, image.originalSize)}%`}
                    </span>
                  </td>
                  <td className="table-cell">
                    {image.compressionLevel}%
                  </td>
                  <td className="table-cell uppercase">
                    {image.format}
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <div className="tooltip-container">
                        <button
                          onClick={() => handleSingleDownload(image)}
                          className="action-button action-button-primary"
                          aria-label={t('common.download')}
                        >
                          <Download size={18} aria-hidden="true" />
                        </button>
                        <div className="tooltip">{t('common.download')}</div>
                      </div>
                      <div className="tooltip-container">
                        <button
                          onClick={() => onRemove(image.id)}
                          className="action-button action-button-danger"
                          aria-label={t('common.remove')}
                        >
                          <X size={18} aria-hidden="true" />
                        </button>
                        <div className="tooltip">{t('common.remove')}</div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default ConvertedImagesTable;